import React from 'react';
import { API_BASE, toUrl } from '../lib/config';

type IntroRow = {
  id: string;
  kicker?: string | null;
  displayGreen?: string | null;
  displayDark?: string | null;
  lede?: string | null;
  watermarkUrl?: string | null;
  isActive: boolean;
  updatedAt: string;
};

type IntroItemRow = {
  id: string;
  introId: string;
  orderIndex: number;
  title: string;
  text: string;
  isActive: boolean;
  updatedAt: string;
};

type MediaOpt = { id?: string; name: string; url: string };

const DoctorsIntroYonetim: React.FC = () => {
  
  const resolveMedia = (u?: string | null) => {
    if (!u) return '';
    // If value is a bare UUID, convert to public media path
    const uuidRe = /^[0-9a-fA-F-]{36}$/;
    const path = uuidRe.test(u) ? `/api/media/${u}` : u;
    return toUrl(path);
  };
  const normalizeWatermarkForSave = (u?: string | null) => {
    if (!u) return null;
    // if full URL or path ends with UUID, extract UUID
    const m = u.match(/([0-9a-fA-F-]{36})$/);
    return m ? m[1] : u; // fallback: keep as-is
  };

  // Image with raw/prefixed toggle fallback (as requested)
  const ImgWithFallback: React.FC<{ src?: string | null; alt: string; height?: number }> = ({ src, alt, height = 40 }) => {
    const baseIn = src || '';
    // If src is UUID or ends with UUID, use /api/media/:id as raw
    const uuidMatch = baseIn.match(/([0-9a-fA-F-]{36})$/);
    const raw = uuidMatch ? `/api/media/${uuidMatch[1]}` : baseIn;
    const pref = toUrl(raw);
    const [currentSrc, setCurrentSrc] = React.useState(pref);
    return (
      <img
        src={currentSrc}
        alt={alt}
        style={{ height, objectFit: 'contain', borderRadius: 6, border: '1px solid #e5e7eb', alignItems: 'center' }}
        crossOrigin="anonymous"
        onError={(e) => {
          const el = e.currentTarget as HTMLImageElement;
          const current = el.getAttribute('src') || '';
          const next = current === pref ? raw : pref;
          if (current !== next) {
            el.src = next;
            setCurrentSrc(next);
          }
        }}
      />
    );
  };

  // Base list
  const [items, setItems] = React.useState<IntroRow[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [q, setQ] = React.useState('');
  const [mediaOpts, setMediaOpts] = React.useState<MediaOpt[]>([]);

  // Inline edit for intro
  const [editingId, setEditingId] = React.useState('');
  const [eKicker, setEKicker] = React.useState('');
  const [eGreen, setEGreen] = React.useState('');
  const [eDark, setEDark] = React.useState('');
  const [eLede, setELede] = React.useState('');
  const [eWatermark, setEWatermark] = React.useState('');
  const [eActive, setEActive] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  // Selected intro rows
  const [selectedIntro, setSelectedIntro] = React.useState<IntroRow | null>(null);
  const [rows, setRows] = React.useState<IntroItemRow[]>([]);
  const [loadingRows, setLoadingRows] = React.useState(false);

  // Inline edit for rows
  const [editingRowId, setEditingRowId] = React.useState('');
  const [rTitle, setRTitle] = React.useState('');
  const [rText, setRText] = React.useState('');
  const [rOrder, setROrder] = React.useState<number>(0);
  const [rActive, setRActive] = React.useState(true);
  const [savingRow, setSavingRow] = React.useState(false);
  const [delRowId, setDelRowId] = React.useState('');
  const [delRowLoading, setDelRowLoading] = React.useState(false);

  const fetchIntros = React.useCallback(async () => {
    try {
      setLoading(true); setError('');
      const res = await fetch(toUrl('/api/doctors-intro/admin'));
      if (!res.ok) throw new Error('Kayıtlar alınamadı');
      const data = await res.json();
      setItems(data || []);
    } catch (e: any) { setError(e.message || 'Beklenmeyen bir hata oluştu'); }
    finally { setLoading(false); }
  }, []);

  const fetchMedia = React.useCallback(async () => {
    try {
      const res = await fetch(toUrl('/api/media/admin'));
      if (!res.ok) return;
      const data = await res.json();
      // Normalize: if url missing, build from id
      setMediaOpts((data || []).map((m: any) => ({ id: m.id, name: m.name || m.originalName || m.id || m.url, url: m.url || `/api/media/${m.id}` })));
    } catch {
      // ignore media errors
    }
  }, []);

  const fetchRows = React.useCallback(async (introId: string) => {
    try {
      setLoadingRows(true); setError('');
      const res = await fetch(toUrl(`/api/doctors-intro/admin/${introId}/rows`));
      if (!res.ok) throw new Error('Rows alınamadı');
      const data = await res.json();
      setRows(data || []);
    } catch (e: any) { setError(e.message || 'Beklenmeyen bir hata oluştu'); }
    finally { setLoadingRows(false); }
  }, []);

  React.useEffect(() => { fetchIntros(); fetchMedia(); }, [fetchIntros, fetchMedia]);

  const filtered = React.useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return items;
    return items.filter(r =>
      (r.kicker || '').toLowerCase().includes(term) ||
      (r.displayGreen || '').toLowerCase().includes(term) ||
      (r.displayDark || '').toLowerCase().includes(term) ||
      (r.lede || '').toLowerCase().includes(term)
    );
  }, [items, q]);

  const openEdit = (row: IntroRow) => {
    setEditingId(row.id);
    setEKicker(row.kicker || '');
    setEGreen(row.displayGreen || '');
    setEDark(row.displayDark || '');
    setELede(row.lede || '');
    setEWatermark(row.watermarkUrl || '');
    setEActive(!!row.isActive);
  };
  const cancelEdit = () => { setEditingId(''); setEKicker(''); setEGreen(''); setEDark(''); setELede(''); setEWatermark(''); setEActive(false); };

  const onSaveIntro = async (id: string) => {
    try {
      setSaving(true); setError('');
      const body: any = {
        kicker: eKicker || null,
        displayGreen: eGreen || null,
        displayDark: eDark || null,
        lede: eLede || null,
        watermarkUrl: normalizeWatermarkForSave(eWatermark),
        isActive: !!eActive,
      };
      const res = await fetch(toUrl(`/api/doctors-intro/admin/${id}`), { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (!res.ok) throw new Error('Güncelleme başarısız');
      cancelEdit(); await fetchIntros();
    } catch (e: any) { setError(e.message || 'Beklenmeyen bir hata oluştu'); }
    finally { setSaving(false); }
  };

  const openRows = (row: IntroRow) => { setSelectedIntro(row); fetchRows(row.id); };
  const closeRows = () => { setSelectedIntro(null); setRows([]); setEditingRowId(''); };

  const openEditRow = (r: IntroItemRow) => { setEditingRowId(r.id); setRTitle(r.title || ''); setRText(r.text || ''); setROrder(r.orderIndex || 0); setRActive(!!r.isActive); };
  const cancelEditRow = () => { setEditingRowId(''); setRTitle(''); setRText(''); setROrder(0); setRActive(true); };
  const onSaveRow = async (id: string) => {
    if (!rTitle.trim() || !rText.trim()) { setError('Lütfen başlık ve metin girin'); return; }
    try {
      setSavingRow(true); setError('');
      const body: any = { title: rTitle.trim(), text: rText.trim(), orderIndex: rOrder || 0, isActive: !!rActive };
      const res = await fetch(toUrl(`/api/doctors-intro/admin/rows/${id}`), { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (!res.ok) throw new Error('Row güncelleme başarısız');
      cancelEditRow(); if (selectedIntro) await fetchRows(selectedIntro.id);
    } catch (e: any) { setError(e.message || 'Beklenmeyen bir hata oluştu'); }
    finally { setSavingRow(false); }
  };
  const onDeleteRow = (id: string) => setDelRowId(id);
  const confirmDeleteRow = async () => {
    if (!delRowId) return;
    try {
      setDelRowLoading(true);
      const res = await fetch(toUrl(`/api/doctors-intro/admin/rows/${delRowId}`), { method: 'DELETE' });
      if (!res.ok) throw new Error('Row silme başarısız');
      setDelRowId(''); if (selectedIntro) await fetchRows(selectedIntro.id);
    } catch (e: any) { setError(e.message || 'Beklenmeyen bir hata oluştu'); }
    finally { setDelRowLoading(false); }
  };

  return (
    <div className="admin-page" style={{ padding: 12 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 12 }}>
        <h2 style={{ margin: 0 }}>Doctors Intro</h2>
        <div style={{ fontSize: 12, color: '#6b7280' }}>Toplam: {items.length}</div>
      </div>

      <div className="card" style={{ padding: 0 }}>
        <div style={{ padding: 12, display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input type="text" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ara: kicker, green, dark, lede" className="input" style={{ width: 320 }} />
            {q && <button onClick={() => setQ('')} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer' }}>Temizle</button>}
          </div>
          <button onClick={fetchIntros} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer' }}>Yenile</button>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f3f4f6' }}>
                <th style={{ textAlign: 'left', padding: '12px 16px' }}>Kicker</th>
                <th style={{ textAlign: 'left', padding: '12px 16px' }}>Green</th>
                <th style={{ textAlign: 'left', padding: '12px 16px' }}>Dark</th>
                <th style={{ textAlign: 'left', padding: '12px 16px' }}>Lede</th>
                <th style={{ textAlign: 'left', padding: '12px 16px' }}>Watermark</th>
                <th style={{ textAlign: 'left', padding: '12px 16px' }}>Aktif</th>
                <th style={{ textAlign: 'left', padding: '12px 16px' }}>Güncellenme</th>
                <th style={{ textAlign: 'right', padding: '12px 16px' }}>İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {loading && (<tr><td colSpan={8} style={{ padding: 16, color: '#6b7280' }}>Yükleniyor...</td></tr>)}
              {!loading && filtered.length === 0 && (<tr><td colSpan={8} style={{ padding: 16, color: '#6b7280' }}>Kayıt bulunamadı.</td></tr>)}
              {filtered.map(row => (
                <tr key={row.id} style={{ borderTop: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '12px 16px', minWidth: 200 }}>
                    {editingId === row.id ? (
                      <textarea className="input" rows={2} value={eKicker} onChange={(e) => setEKicker(e.target.value)} />
                    ) : (row.kicker || '-')}
                  </td>
                  <td style={{ padding: '12px 16px', minWidth: 200 }}>
                    {editingId === row.id ? (
                      <textarea className="input" rows={2} value={eGreen} onChange={(e) => setEGreen(e.target.value)} />
                    ) : (row.displayGreen || '-')}
                  </td>
                  <td style={{ padding: '12px 16px', minWidth: 200 }}>
                    {editingId === row.id ? (
                      <textarea className="input" rows={2} value={eDark} onChange={(e) => setEDark(e.target.value)} />
                    ) : (row.displayDark || '-')}
                  </td>
                  <td style={{ padding: '12px 16px', minWidth: 240 }}>
                    {editingId === row.id ? (
                      <textarea className="input" rows={3} value={eLede} onChange={(e) => setELede(e.target.value)} />
                    ) : (row.lede || '-')}
                  </td>
                  <td style={{ padding: '12px 16px', minWidth: 240 }}>
                    {editingId === row.id ? (
                      <div style={{ display: 'grid', gap: 6 }}>
                        <select className="input" value={eWatermark || ''} onChange={(e) => setEWatermark(e.target.value)}>
                          <option value="">Seçiniz</option>
                          {mediaOpts.map(m => (
                            <option key={(m.id || m.url)} value={(m.id || m.url)}>{m.name}</option>
                          ))}
                        </select>
                        {eWatermark && (<ImgWithFallback src={eWatermark} alt="watermark" height={40} />)}
                      </div>
                    ) : (
                      row.watermarkUrl ? (
                        <ImgWithFallback src={row.watermarkUrl} alt="watermark" height={60} />
                      ) : '-'
                    )}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    {editingId === row.id ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <input id={`act-${row.id}`} type="checkbox" checked={eActive} onChange={(e) => setEActive(e.target.checked)} />
                        <label htmlFor={`act-${row.id}`}>Aktif</label>
                      </div>
                    ) : (
                      <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 999, fontSize: 12, background: row.isActive ? '#ecfdf5' : '#fef2f2', color: row.isActive ? '#065f46' : '#991b1b', border: `1px solid ${row.isActive ? '#a7f3d0' : '#fecaca'}` }}>{row.isActive ? 'Aktif' : 'Pasif'}</span>
                    )}
                  </td>
                  <td style={{ padding: '12px 16px' }}>{row.updatedAt ? new Date(row.updatedAt).toLocaleString() : '-'}</td>
                  <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                    {editingId === row.id ? (
                      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                        <button onClick={() => onSaveIntro(row.id)} disabled={saving} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #16a34a', background: '#16a34a', color: '#fff', cursor: 'pointer', opacity: saving ? 0.8 : 1 }}>{saving ? 'Kaydediliyor...' : 'Kaydet'}</button>
                        <button onClick={cancelEdit} disabled={saving} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer' }}>İptal</button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                        <button onClick={() => openRows(row)} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #0ea5e9', background: '#fff', color: '#0ea5e9', cursor: 'pointer' }}>İçerikler</button>
                        <button onClick={() => openEdit(row)} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #2563eb', background: '#fff', color: '#2563eb', cursor: 'pointer' }}>Düzenle</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Rows for selected intro */}
      {selectedIntro && (
        <div className="card" style={{ padding: 16, marginTop: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <h4 style={{ margin: 0 }}>Seçili Intro</h4>
            <button type="button" onClick={closeRows} aria-label="Kapat" style={{ background: 'transparent', border: 0, fontSize: 18, lineHeight: 1, cursor: 'pointer', color: '#6b7280' }}>×</button>
          </div>
          <div className="card" style={{ padding: 0 }}>
            <div style={{ padding: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontWeight: 600 }}>Rows</div>
              <button onClick={() => fetchRows(selectedIntro.id)} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer' }}>Yenile</button>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f3f4f6' }}>
                    <th style={{ textAlign: 'left', padding: '12px 16px' }}>Başlık</th>
                    <th style={{ textAlign: 'left', padding: '12px 16px' }}>Metin</th>
                    <th style={{ textAlign: 'left', padding: '12px 16px' }}>Sıra</th>
                    <th style={{ textAlign: 'left', padding: '12px 16px' }}>Aktif</th>
                    <th style={{ textAlign: 'left', padding: '12px 16px' }}>Güncellenme</th>
                    <th style={{ textAlign: 'right', padding: '12px 16px' }}>İşlemler</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingRows && (<tr><td colSpan={6} style={{ padding: 16, color: '#6b7280' }}>Yükleniyor...</td></tr>)}
                  {!loadingRows && rows.length === 0 && (<tr><td colSpan={6} style={{ padding: 16, color: '#6b7280' }}>Kayıt bulunamadı.</td></tr>)}
                  {rows.map(r => (
                    <tr key={r.id} style={{ borderTop: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '12px 16px', minWidth: 220 }}>
                        {editingRowId === r.id ? (
                          <input className="input" value={rTitle} onChange={(e) => setRTitle(e.target.value)} />
                        ) : r.title}
                      </td>
                      <td style={{ padding: '12px 16px', minWidth: 260 }}>
                        {editingRowId === r.id ? (
                          <textarea className="input" rows={3} value={rText} onChange={(e) => setRText(e.target.value)} />
                        ) : r.text}
                      </td>
                      <td style={{ padding: '12px 16px', width: 120 }}>
                        {editingRowId === r.id ? (
                          <input className="input" type="number" value={rOrder} onChange={(e) => setROrder(parseInt(e.target.value || '0', 10))} />
                        ) : r.orderIndex}
                      </td>
                      <td style={{ padding: '12px 16px', width: 160 }}>
                        {editingRowId === r.id ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <input id={`rAct-${r.id}`} type="checkbox" checked={rActive} onChange={(e) => setRActive(e.target.checked)} />
                            <label htmlFor={`rAct-${r.id}`}>Aktif</label>
                          </div>
                        ) : (
                          <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 999, fontSize: 12, background: r.isActive ? '#ecfdf5' : '#fef2f2', color: r.isActive ? '#065f46' : '#991b1b', border: `1px solid ${r.isActive ? '#a7f3d0' : '#fecaca'}` }}>{r.isActive ? 'Aktif' : 'Pasif'}</span>
                        )}
                      </td>
                      <td style={{ padding: '12px 16px' }}>{r.updatedAt ? new Date(r.updatedAt).toLocaleString() : '-'}</td>
                      <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                        {editingRowId === r.id ? (
                          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                            <button onClick={() => onSaveRow(r.id)} disabled={savingRow} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #16a34a', background: '#16a34a', color: '#fff', cursor: 'pointer', opacity: savingRow ? 0.8 : 1 }}>{savingRow ? 'Kaydediliyor...' : 'Kaydet'}</button>
                            <button onClick={cancelEditRow} disabled={savingRow} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer' }}>İptal</button>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                            <button onClick={() => openEditRow(r)} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #2563eb', background: '#fff', color: '#2563eb', cursor: 'pointer' }}>Düzenle</button>
                            <button onClick={() => onDeleteRow(r.id)} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #dc2626', background: '#dc2626', color: '#fff', cursor: 'pointer' }}>Sil</button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Delete Row Confirm Modal */}
      {delRowId && (
        <div role="dialog" aria-modal="true" aria-labelledby="rowDelTitle" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1050 }} onClick={(e) => { if (e.target === e.currentTarget) setDelRowId(''); }} onKeyDown={(e) => { if (e.key === 'Escape') setDelRowId(''); }} tabIndex={-1}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: 'min(480px,92vw)', background: '#fff', borderRadius: 12, boxShadow: '0 10px 25px rgba(0,0,0,0.15)', overflow: 'hidden' }}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h5 id="rowDelTitle" style={{ margin: 0, fontSize: 18 }}>Row Silme Onayı</h5>
              <button type="button" aria-label="Close" onClick={() => setDelRowId('')} style={{ background: 'transparent', border: 0, fontSize: 18, lineHeight: 1, cursor: 'pointer', color: '#6b7280' }}>×</button>
            </div>
            <div style={{ padding: 16 }}>Bu row kaydını silmek istediğinize emin misiniz? Bu işlem geri alınamaz.</div>
            <div style={{ padding: 12, borderTop: '1px solid #e5e7eb', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setDelRowId('')} disabled={delRowLoading} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer' }}>İptal</button>
              <button type="button" onClick={confirmDeleteRow} disabled={delRowLoading} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #dc2626', background: '#dc2626', color: '#fff', cursor: 'pointer', opacity: delRowLoading ? 0.8 : 1 }}>{delRowLoading ? 'Siliniyor...' : 'Evet, Sil'}</button>
            </div>
          </div>
        </div>
      )}

      {error && <div style={{ marginTop: 8, color: '#b00020' }}>{error}</div>}
    </div>
  );
};

export default DoctorsIntroYonetim;
